import { AutoLinkNode, LinkNode } from '@lexical/link';
import { AutoLinkPlugin } from "@lexical/react/LexicalAutoLinkPlugin";
import { ClearEditorPlugin } from '@lexical/react/LexicalClearEditorPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import {
    $createParagraphNode,
    $createTextNode,
    $getRoot,
    CLEAR_EDITOR_COMMAND,
    CONTROLLED_TEXT_INSERTION_COMMAND
} from "lexical";
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { MentionNode } from "../../../../../libs/lexical/plugins/mention/MentionNode";
import MentionsPlugin from "../../../../../libs/lexical/plugins/mention/MentionsPlugin";
import styles from './MediaEditor.module.scss';

const URL_MATCHER =
    /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/;

const MATCHERS = [
    (text) => {
        const match = URL_MATCHER.exec(text);
        if (match === null) {
            return null;
        }
        const fullMatch = match[0];
        return {
            index: match.index,
            length: fullMatch.length,
            text: fullMatch,
            url: fullMatch.startsWith('http') ? fullMatch : `https://${fullMatch}`,
            attributes: { rel: 'noreferrer', target: '_blank' }, // Optional link attributes
        };
    },
];

function onError(error) {
    console.error(error);
}

function RefPlugin({ editorRef }) {
    const [editor] = useLexicalComposerContext()
    editorRef.current = editor
    return null;
}

function InsertText({text = ""}) {
    const [editor] = useLexicalComposerContext();

    const initialTextRef = useRef(text);

    useEffect(() => {
        editor.update(() => {
            const root = $getRoot();
            const paragraph = $createParagraphNode();
            paragraph.append($createTextNode(initialTextRef.current));
            root.clear();
            paragraph.select();
            root.append(paragraph);
        });
    }, [editor]); // Se déclenche uniquement au montage

    return null
}

const MediaEditor = ({onChange=(editorState)=>{}, text, mentions = [], links = []}, ref)=>{
    const initialConfig = {
        namespace: 'MyEditor',
        onError,
        nodes: [
            MentionNode,
            AutoLinkNode,
            LinkNode
        ]
    };

    const editorRef = useRef();
    useImperativeHandle(ref, () => ({
        insert: (text) => {
            const editor = editorRef.current;
            editor.focus();
            editor.dispatchCommand(CLEAR_EDITOR_COMMAND);
            editor.dispatchCommand(CONTROLLED_TEXT_INSERTION_COMMAND, text);
        }
    }));
    return (
        <LexicalComposer initialConfig={initialConfig}>
            <div className={styles["media-editor"]} >
                <div className={styles["editor-wrapper"]}>
                    <div className={styles["editor-container"]}>
                        <InsertText text={text}/>
                        <PlainTextPlugin
                            contentEditable={<ContentEditable className={styles["editor-input"]}/>}
                            placeholder={<div className={styles["editor-placeholder"]}>Message</div>}
                            ErrorBoundary={LexicalErrorBoundary}
                        />
                        <RefPlugin editorRef={editorRef}/>
                        <OnChangePlugin onChange={onChange} ignoreSelectionChange={true}/>
                        <HistoryPlugin />
                        <ClearEditorPlugin/>
                        <AutoLinkPlugin matchers={MATCHERS} />
                        <LinkPlugin />
                        <MentionsPlugin mentions={mentions}/>
                    </div>
                </div>
            </div>
        </LexicalComposer>
    );
}

export default forwardRef(MediaEditor)